import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { 
  ArrowLeft, Clock, CheckCircle, XCircle, Trophy, 
  RotateCcw, Play, Zap, Target
} from "lucide-react";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MathDrill() {
  const { drillId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Game state
  const [gameState, setGameState] = useState("loading"); // loading, ready, playing, completed
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { index: { answer, correct, timeMs, retryUsed } }
  const [userInput, setUserInput] = useState("");
  const [retryAvailable, setRetryAvailable] = useState(true);
  
  // Timer state
  const [timeLeft, setTimeLeft] = useState(0);
  const [timePerQuestion, setTimePerQuestion] = useState(10);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  
  // Stats
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  
  // Results
  const [results, setResults] = useState(null);
  
  const inputRef = useRef(null);

  // Load drill and start session
  const loadDrill = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/drills/${drillId}/start`);
      setSession(response.data);
      setQuestions(response.data.questions);
      setTimePerQuestion(response.data.time_per_question);
      setGameState("ready");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load drill");
      navigate("/dashboard");
    }
  }, [drillId, navigate]);

  useEffect(() => {
    loadDrill();
  }, [loadDrill]);

  // Start the game
  const startGame = () => {
    setGameState("playing");
    setTimeLeft(timePerQuestion);
    startTimeRef.current = Date.now();
    
    // Focus input
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Handle timeout - wrapped in useCallback
  const handleTimeout = useCallback(() => {
    if (gameState !== "playing") return;
    
    const currentQ = questions[currentIndex];
    const timeMs = startTimeRef.current ? Date.now() - startTimeRef.current : 0;
    
    setAnswers(prev => ({
      ...prev,
      [currentIndex]: {
        ...currentQ,
        user_answer: null,
        is_correct: false,
        time_ms: timeMs,
        retry_used: !retryAvailable
      }
    }));
    
    setWrongCount(prev => prev + 1);
  }, [gameState, questions, currentIndex, retryAvailable]);

  // Timer logic
  useEffect(() => {
    if (gameState !== "playing") return;
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up - will handle in separate effect
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timerRef.current);
  }, [gameState, currentIndex, timePerQuestion]);

  // Handle when time runs out
  useEffect(() => {
    if (timeLeft === 0 && gameState === "playing") {
      handleTimeout();
      // Move to next after timeout
      if (currentIndex + 1 >= questions.length) {
        completeGameRef.current();
      } else {
        setCurrentIndex(prev => prev + 1);
        setUserInput("");
        setRetryAvailable(true);
        setTimeLeft(timePerQuestion);
        startTimeRef.current = Date.now();
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [timeLeft, gameState, handleTimeout, currentIndex, questions.length, timePerQuestion]);

  // Use ref for completeGame to avoid dependency issues
  const completeGameRef = useRef(null);
  
  const completeGame = useCallback(async () => {
    clearInterval(timerRef.current);
    setGameState("completed");
    
    // Get current answers from state
    const questionResults = Object.values(answers);
    const totalTime = questionResults.reduce((sum, q) => sum + (q.time_ms || 0), 0) / 1000;
    
    try {
      const response = await axios.post(`${API}/drills/${drillId}/submit`, {
        questions: questionResults,
        total_time_seconds: Math.round(totalTime)
      });
      setResults(response.data);
    } catch (error) {
      toast.error("Failed to save results");
      setResults({
        results: {
          correct: correctCount,
          wrong: wrongCount,
          total: questions.length,
          accuracy: questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0
        }
      });
    }
  }, [drillId, answers, correctCount, wrongCount, questions.length]);

  // Keep ref updated
  useEffect(() => {
    completeGameRef.current = completeGame;
  }, [completeGame]);

  const moveToNextQuestion = useCallback(() => {
    if (currentIndex + 1 >= questions.length) {
      completeGame();
    } else {
      setCurrentIndex(prev => prev + 1);
      setUserInput("");
      setRetryAvailable(true);
      setTimeLeft(timePerQuestion);
      startTimeRef.current = Date.now();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [currentIndex, questions.length, timePerQuestion, completeGame]);

  const handleSubmit = useCallback(() => {
    if (!userInput.trim()) return;
    
    const currentQ = questions[currentIndex];
    const userAnswer = parseInt(userInput);
    const isCorrect = userAnswer === currentQ.correct_answer;
    const timeMs = startTimeRef.current ? Date.now() - startTimeRef.current : 0;
    
    if (isCorrect) {
      // Correct answer
      setAnswers(prev => ({
        ...prev,
        [currentIndex]: {
          ...currentQ,
          user_answer: userAnswer,
          is_correct: true,
          time_ms: timeMs,
          retry_used: !retryAvailable
        }
      }));
      setCorrectCount(prev => prev + 1);
      moveToNextQuestion();
    } else {
      // Wrong answer
      if (retryAvailable) {
        // Allow one retry
        toast.error("Try again! One more chance.");
        setRetryAvailable(false);
        setUserInput("");
        inputRef.current?.focus();
      } else {
        // No more retries
        setAnswers(prev => ({
          ...prev,
          [currentIndex]: {
            ...currentQ,
            user_answer: userAnswer,
            is_correct: false,
            time_ms: timeMs,
            retry_used: true
          }
        }));
        setWrongCount(prev => prev + 1);
        moveToNextQuestion();
      }
    }
  }, [userInput, questions, currentIndex, retryAvailable, moveToNextQuestion]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  // Calculate progress
  const progressPercent = questions.length > 0 
    ? ((currentIndex + (gameState === "completed" ? 1 : 0)) / questions.length) * 100 
    : 0;

  // Loading state
  if (gameState === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading drill...</p>
        </div>
      </div>
    );
  }

  // Ready state - show start screen
  if (gameState === "ready") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <Card className="shadow-xl">
            <CardHeader className="text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-10 h-10 text-indigo-600" />
              </div>
              <CardTitle className="text-2xl">{session?.drill_name}</CardTitle>
              <p className="text-gray-500 mt-2">Math Multiplication Drill</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-3xl font-bold text-indigo-600">{session?.total_questions}</p>
                  <p className="text-sm text-gray-500">Questions</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-3xl font-bold text-indigo-600">{timePerQuestion}s</p>
                  <p className="text-sm text-gray-500">Per Question</p>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Rules:</strong> You have {timePerQuestion} seconds per question. 
                  You get <strong>one retry</strong> if you answer incorrectly.
                </p>
              </div>
              
              <Button 
                onClick={startGame}
                className="w-full h-14 text-lg gap-2 bg-indigo-600 hover:bg-indigo-700"
              >
                <Play className="w-5 h-5" />
                Start Drill
              </Button>
              
              <Button 
                variant="ghost" 
                onClick={() => navigate("/dashboard")}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Completed state - show results
  if (gameState === "completed" && results) {
    const accuracy = results.results?.accuracy || 0;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full"
        >
          <Card className="shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-12 h-12 text-yellow-500" />
              </div>
              <CardTitle className="text-2xl">Drill Complete!</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Score */}
              <div className="text-center">
                <p className="text-5xl font-bold text-indigo-600">{accuracy}%</p>
                <p className="text-gray-500">Accuracy</p>
              </div>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-600">{results.results?.correct}</p>
                  <p className="text-xs text-gray-500">Correct</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-red-600">{results.results?.wrong}</p>
                  <p className="text-xs text-gray-500">Wrong</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-blue-600">{results.results?.total}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
              </div>
              
              {/* Improvement Message */}
              <div className="bg-indigo-50 rounded-lg p-4 text-center">
                <p className="text-lg">{results.improvement_message}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Next drill: <strong>{results.next_time_per_question}s</strong> per question
                </p>
              </div>
              
              {/* Problem Areas */}
              {results.problem_areas?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Practice these:</p>
                  <div className="flex flex-wrap gap-2">
                    {results.problem_areas.slice(0, 6).map((p, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-mono"
                      >
                        {p.a} × {p.b} = {p.a * p.b}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  onClick={() => window.location.reload()}
                  className="flex-1 gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Try Again
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="flex-1"
                >
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Playing state - main game UI
  const currentQuestion = questions[currentIndex];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      {/* Header with progress */}
      <header className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-2">
            <h1 className="text-lg font-bold text-gray-800">{session?.drill_name}</h1>
            <div className="flex items-center gap-4">
              <span className="text-green-600 font-semibold">{correctCount} ✓</span>
              <span className="text-red-600 font-semibold">{wrongCount} ✗</span>
              <span className="text-gray-600">{currentIndex + 1}/{questions.length}</span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
            <div 
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${(correctCount / questions.length) * 100}%` }}
            />
            <div 
              className="h-full bg-red-500 transition-all duration-300"
              style={{ width: `${(wrongCount / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </header>

      {/* Main game area */}
      <main className="flex items-center justify-center min-h-[calc(100vh-100px)] p-4">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <Card className="shadow-xl">
            <CardContent className="p-8">
              {/* Timer */}
              <div className="flex justify-center mb-6">
                <div className={`
                  w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold
                  ${timeLeft <= 3 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-indigo-100 text-indigo-600'}
                `}>
                  <Clock className="w-5 h-5 mr-1" />
                  {timeLeft}
                </div>
              </div>
              
              {/* Question */}
              <div className="text-center mb-8">
                <p className="text-6xl font-bold text-gray-800 mb-2">
                  {currentQuestion?.a} <span className="text-gray-400">×</span> {currentQuestion?.b}
                </p>
                <p className="text-sm text-gray-500">
                  {retryAvailable ? "Type your answer" : "Last chance!"}
                </p>
              </div>
              
              {/* Input */}
              <div className="flex gap-3">
                <Input
                  ref={inputRef}
                  type="number"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="text-center text-3xl h-16 font-mono"
                  placeholder="?"
                  autoFocus
                />
                <Button 
                  onClick={handleSubmit}
                  className="h-16 px-8 bg-indigo-600 hover:bg-indigo-700"
                >
                  <CheckCircle className="w-6 h-6" />
                </Button>
              </div>
              
              {/* Retry indicator */}
              {!retryAvailable && (
                <p className="text-center text-amber-600 mt-4 text-sm">
                  ⚠️ Retry used - this is your final answer
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
